import 'vue-swatches/dist/vue-swatches.min.css'
import './drawing-editor.scss'

import _ from 'lodash'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import Swatches from 'vue-swatches'
import { faArrowLeft, faCheck, faCircleNotch, faLongArrowAltLeft, faPalette, faPen } from '@fortawesome/free-solid-svg-icons'
import { faCircle, faImage, faSquare, faTimesCircle } from '@fortawesome/free-regular-svg-icons'

import { Alteration, createTranslatedString, getTranslatedString } from '@skinsoft/types'
import { arrayBufferToBase64, extractAndRemoveExifOrientationFromMedia, getImageOrientation, ImageTransferHelper } from '@skinsoft/media-transfer-helper'
import { ReactiveVue } from '@skinsoft/reactive-vue'
import { Media } from '@skinsoft/ui/src/item-card/media'
import { isImageOrientationAutoManaged } from '@skinsoft/utils'
import { ArrowDrawingTool, CircleDrawingTool, DrawingTool, DrawingToolType, FreeDrawingTool, RectangleDrawingTool, ShapeConfig } from '@skinsoft/entity-ui/src/condition-report/drawing-toolbox'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus/src/busTopics'
import { CLOSE_DRAWING_EDITOR_TOPIC } from '../../../../busTopics'
import { AnnotationDrawing } from '../annotation-drawing'
import { ALTERATION_EDITED, AlterationChanged, DELETE_ALTERATION } from '../alteration-line'
import AlterationList from '../../alteration-area/alteration-list'

export interface DrawingEditorClosed {
    saveReport: boolean,
    newData?: Alteration[],
}

@Component({
    components: {
        sAnnotationDrawing: AnnotationDrawing,
        sMedia: Media,
        sAlterationList: AlterationList,
        swatches: Swatches
    },
    props: ['mediaId', 'mediaAlterations', 'mediaUrl', 'readOnly'],
    template: require('./drawing-editor.html')
})
export class DrawingEditor extends ReactiveVue {
    // Icons
    public backIcon = faArrowLeft;
    public circleIcon = faCircle;
    public arrowIcon = faLongArrowAltLeft;
    public curveIcon = faPen;
    public squareIcon = faSquare;
    public crossCircleIcon = faTimesCircle;
    public checkIcon = faCheck;
    public colorIcon = faPalette;
    public imageIcon = faImage;
    public loadingIcon = faCircleNotch;

    private readOnly: Boolean
    public mediaUrl: string;

    private mediaId: string
    private mediaAlterations: Alteration[]
    private alterationsCopy: Alteration[] = []

    public image = new Image()
    public configImage: any = {};
    public $refs: { stage: any }

    public $imageTransferHelper: ImageTransferHelper

    // Drawing
    private readonly DEFAULT_STROKE_COLOR = "#f15f22";
    private strokeColor = this.DEFAULT_STROKE_COLOR;
    private currentToolType: DrawingToolType = DrawingToolType.CIRCLE
    private currentTool: DrawingTool = null as any;
    private selectedToolClass = "selectedTool"
    private toolBox: Map<DrawingToolType, DrawingTool> = new Map()
    private drawings: ShapeConfig[][] = [];
    private container = null as any;
    private stage = null as any;
    private stageScaleFactor = 1;
    private stageOffset = { x: 0, y: 0 };
    private startClick: any = undefined;
    private currentDrawing: any = undefined;
    private isDrawing: boolean = false;
    private orientation: number = 1;
    private isDrawingFirstShape = true

    // Responsive computing default values
    private strokeWidth: number = 5;
    private arrowPointerWidth: number = 20;
    private arrowPointerLength: number = 20;
    private annotationLabelConfig = {
        fontSize: 18 as number,
        radius: 15 as number,
        strokeWidth: 2 as number
    }
    private editorOrientationClass = "";
    private canvasOrientationClass = "";

    // The ratios represent a relative size compared to the image's width, so that 
    // the apparent size of annotation and labels is the same for all kind of image dimensions
    private readonly STROKE_WIDTH_RATIO: number = 0.005;
    private readonly ANNOTATION_LABEL_RADIUS_RATIO = 0.02;
    private readonly ANNOTATION_LABEL_FONT_SIZE_RATIO = 0.02;
    private readonly ANNOTATION_LABEL_STROKE_WIDTH_RATIO = 0.002;
    private readonly ARROW_POINTER_LENGTH_RATIO = 0.02
    private readonly ARROW_POINTER_WIDTH_RATIO = 0.02

    private ready: boolean = false;

    // The factor used to limit the number of points in a free drawing
    private freeDrawingSimplificationFactor: number = 1.5;
    private readonly FREE_DRAWING_SIMPLIFICATION_FACTOR_RATIO = 0.002;

    private configKonva = {
        height: 50,
        width: 50,
    }

    private DELETE_ALTERATION = DELETE_ALTERATION
    private ALTERATION_EDITED = ALTERATION_EDITED

    public mounted() {
        this.fetchMedia();
        this.alterationsCopy = _.cloneDeep(this.mediaAlterations)

        // getting drawings
        this.drawings = this.mediaAlterations.map(alteration => {
            try {
                return JSON.parse(alteration.drawing)
            } catch {
                return []
            }
        })
        if (!this.drawings) {
            this.drawings = []
        }
        this.renumberShapes()
    }

    private changeDrawingTool(tool: DrawingToolType) {
        this.currentToolType = tool
    }

    /**
     * Allows to make the Konva stage fill the window properly
     */
    private resizeStage() {
        if (this.stage && this.container) {

            this.adaptCanvasSizeToImageOrientation()

            // Take orientation into account to retrieve real width of image (chrome 81 issue)
            const refDimension = this.orientation === 6 || this.orientation === 8 ? this.image.width : this.image.height

            // setting drawing values for responsive behavior
            this.strokeWidth = this.STROKE_WIDTH_RATIO * refDimension;
            this.arrowPointerLength = this.ARROW_POINTER_LENGTH_RATIO * refDimension;
            this.arrowPointerWidth = this.ARROW_POINTER_WIDTH_RATIO * refDimension;
            this.annotationLabelConfig.fontSize = this.ANNOTATION_LABEL_FONT_SIZE_RATIO * refDimension;
            this.annotationLabelConfig.radius = this.ANNOTATION_LABEL_RADIUS_RATIO * refDimension;
            this.annotationLabelConfig.strokeWidth = this.ANNOTATION_LABEL_STROKE_WIDTH_RATIO * refDimension;
            this.freeDrawingSimplificationFactor = this.FREE_DRAWING_SIMPLIFICATION_FACTOR_RATIO * refDimension;

            // Make editor display only when resizing stuff is done
            this.ready = true
        }
    }

    /**
     * Makes the canvas fit the contained image, taking the
     * image orientation into account
     */
    private adaptCanvasSizeToImageOrientation() {
        const containerSize = {
            height: this.container.clientHeight,
            width: this.container.clientWidth
        };
        let imageRatio = 1;
        switch (this.orientation) {
            // Vertical images
            case 6:
            case 8:
                {
                    this.stageScaleFactor = containerSize.height / this.image.width;
                    imageRatio = this.image.width / this.image.height;
                    this.stage.scale({ x: this.stageScaleFactor, y: this.stageScaleFactor })
                    this.configKonva.height = containerSize.height;
                    this.configKonva.width = this.configKonva.height / imageRatio;
                    break;
                }
            // Horizontal images
            default:
                {
                    // Vertical images with orientation 1 
                    if (this.image.height > this.image.width) {
                        this.stageScaleFactor = containerSize.height / this.image.height;
                        imageRatio = this.image.height / this.image.width;
                        this.stage.scale({ x: this.stageScaleFactor, y: this.stageScaleFactor })
                        this.configKonva.height = containerSize.height;
                        this.configKonva.width = this.configKonva.height / imageRatio;
                    } else {
                        this.stageScaleFactor = containerSize.width / this.image.width;
                        imageRatio = this.image.width / this.image.height;
                        this.stage.scale({ x: this.stageScaleFactor, y: this.stageScaleFactor })
                        this.configKonva.width = containerSize.width;
                        this.configKonva.height = this.configKonva.width / imageRatio;
                        break;
                    }
                }
        }
    }

    private fetchMedia() {
        if(this.mediaUrl) {
            this.setupMedia(this.mediaUrl)
        } else {
            if(window.navigator.onLine || this.mediaId.startsWith('local:')) {
                this.subscribeTo(this.$imageTransferHelper.downloadImage(this.mediaId, "proxy"),
                    mediaSrc => {
                        this.setupMedia(mediaSrc.dataUrl)
                    },
                    'media',
                );
    
            } else {
                this.subscribeTo(this.$imageTransferHelper.downloadImageOffline(this.mediaId),
                    mediaSrc => {
                        this.setupMedia(mediaSrc.dataUrl)
                    },
                    'media',
                )
            }
        }
    }

    private setupMedia(mediaSrc) {
        // strip exif rotation data and rotate media
        let base64Media: string = mediaSrc
        const { orientation, mediaWithoutOrientation } = extractAndRemoveExifOrientationFromMedia(mediaSrc)
        if(orientation != -1) {
            if(mediaWithoutOrientation instanceof String || typeof mediaWithoutOrientation === 'string') {
                base64Media = mediaWithoutOrientation as string
            } else {
                base64Media = arrayBufferToBase64(mediaWithoutOrientation)
            }
        }

        this.image.src = base64Media
        this.image.onload = () => {
            this.fixImageOrientation(
                {
                    height: this.image.height,
                    image: this.image,
                    width: this.image.width,
                    x: 0,
                    y: 0,
                }, 
                orientation != -1 ? orientation : undefined
            )

            this.stage = this.$refs.stage.getStage()
            this.container = this.stage.container();

            // Force canvas resizing and redrawing after Vue render
            this.$nextTick().then(() => this.resizeStage())
            // Listening to window resize event to trigger stage redrawing
            window.addEventListener('resize', this.resizeStage)
        }
    }

    /**
     * Adapts the orientation of the image according to the
     * EXIF orientation metadata
     * @param baseImageConfig 
     */
    // tslint:disable-next-line:member-ordering
    public async fixImageOrientation(baseImageConfig, orientation?: number) {
        if(orientation) {
            this.orientation = orientation
        } else {
            this.orientation = isImageOrientationAutoManaged() ? 1 : await getImageOrientation(baseImageConfig.image)
        }
        switch (this.orientation) {
            case 3:
                this.configImage = {
                    ...baseImageConfig,

                    offsetX: baseImageConfig.width,
                    offsetY: baseImageConfig.height,
                    rotation: 180,
                }
                this.editorOrientationClass = "drawing-editor-h"
                this.canvasOrientationClass = "drawing-canvas-h"
                break;
            case 6:
                this.configImage = {
                    ...baseImageConfig,

                    offsetX: 0,
                    offsetY: baseImageConfig.height,
                    rotation: 90,
                }
                this.editorOrientationClass = "drawing-editor-v"
                this.canvasOrientationClass = "drawing-canvas-v"
                break;
            case 8:
                this.configImage = {
                    ...baseImageConfig,

                    offsetX: baseImageConfig.width,
                    offsetY: 0,
                    rotation: -90,
                }
                this.editorOrientationClass = "drawing-editor-v"
                this.canvasOrientationClass = "drawing-canvas-v"
                break;

            default:
                if (this.image.height > this.image.width) {
                    this.configImage = baseImageConfig;
                    this.editorOrientationClass = "drawing-editor-v"
                    this.canvasOrientationClass = "drawing-canvas-v"
                } else {
                    this.configImage = baseImageConfig;
                    this.editorOrientationClass = "drawing-editor-h"
                    this.canvasOrientationClass = "drawing-canvas-h"
                }
                break;
        }
    }

    private closeDrawingEditor(save: boolean) {
        let closeEventData: DrawingEditorClosed = {
            saveReport: save
        }
        if(save) {
            closeEventData.newData = this.alterationsCopy
        }

        window.removeEventListener('resize', this.resizeStage);
        this.emitOnBus(CLOSE_DRAWING_EDITOR_TOPIC, closeEventData)
    }

    private onTouchStart(event) {
        if(!this.readOnly) {
            if (event.currentTarget._pointerPositions) {
                this.startClick = {}
                this.startClick.x = (event.currentTarget._changedPointerPositions[0].x - this.stageOffset.x) / this.stageScaleFactor
                this.startClick.y = (event.currentTarget._changedPointerPositions[0].y - this.stageOffset.y) / this.stageScaleFactor
                
                this.chooseTool(this.currentToolType)
                let newShape = this.currentTool.addShape(this.startClick.x, this.startClick.y, this.drawings.length, this.strokeColor)
                
                if(this.isDrawingFirstShape) {
                    this.currentDrawing = [newShape]
                
                    this.addAlteration()
                    this.drawings.push(this.currentDrawing)
                    if(this.currentToolType === DrawingToolType.FREE_DRAWING) {
                        this.isDrawingFirstShape = false
                    }
                } else {
                    this.currentDrawing.push(newShape)    
                }
    
                this.isDrawing = true;
            } else {
                this.startClick = undefined
            }
        }
    }

    private onTouchMove(event) {
        if(!this.readOnly) {
            if (this.isDrawing && event.currentTarget._changedPointerPositions) {
    
                const currentX = (event.currentTarget._changedPointerPositions[0].x - this.stageOffset.x) / this.stageScaleFactor
                const currentY = (event.currentTarget._changedPointerPositions[0].y - this.stageOffset.y) / this.stageScaleFactor
    
                const shape = this.currentDrawing[this.currentDrawing.length - 1]
                this.currentTool.onTouchMove(shape, currentX, currentY, this.startClick.x, this.startClick.y);
            }
        }
    }

    private onTouchEnd(event) {
        if(!this.readOnly) {
            if (this.startClick && event.currentTarget._changedPointerPositions) {
    
                const endX = (event.currentTarget._changedPointerPositions[0].x - this.stageOffset.x) / this.stageScaleFactor
                const endY = (event.currentTarget._changedPointerPositions[0].y - this.stageOffset.y) / this.stageScaleFactor
                const shape = this.currentDrawing[this.currentDrawing.length - 1]
                this.currentTool.onTouchEnd(shape, endX, endY, this.startClick.x, this.startClick.y)
            }
            this.isDrawing = false;
        }
    }

    private onValidateDrawing() {
        this.isDrawingFirstShape = true
    }

    private chooseTool(toolType: DrawingToolType) {
        if (!this.toolBox.get(toolType)) {
            switch (toolType) {
                case DrawingToolType.ARROW:
                    this.toolBox.set(toolType, new ArrowDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR, this.arrowPointerLength, this.arrowPointerWidth))
                    break;
                case DrawingToolType.CIRCLE:
                    this.toolBox.set(toolType, new CircleDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR))
                    break;
                case DrawingToolType.RECTANGLE:
                    this.toolBox.set(toolType, new RectangleDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR))
                    break;
                case DrawingToolType.FREE_DRAWING:
                    this.toolBox.set(toolType, new FreeDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR, false, this.freeDrawingSimplificationFactor))
                    break;
                default:
                    this.toolBox.set(toolType, new CircleDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR));
                    break;
            }
        }
        const tool = this.toolBox.get(toolType)
        if (tool) {
            this.currentTool = tool;
        }
    }

    private addAlteration() {
        let newAlteration: Alteration = {
            media: this.mediaId,
            drawing: '',
            detailedViews: [],
            //@ts-ignore
            typeOfAlteration: null,
            precision: createTranslatedString(this.$i18n.locale, ''),
            title: createTranslatedString(this.$i18n.locale, ''),
        }
        this.alterationsCopy.push(newAlteration)
    }

    private deleteAlteration(index) {
        this.drawings.splice(index, 1);
        this.alterationsCopy.splice(index, 1)
    }

    private onAlterationChanged(event: AlterationChanged) {
        const oldValue = this.alterationsCopy[event.index]
        this.alterationsCopy[event.index] = _.merge(oldValue, event.updates)
    }

    private isToolSelected(tool: DrawingToolType) {
        return this.currentToolType === tool;
    }

    private validate() {
        const valid = this.alterationsCopy.every(alteration => {
            return (getTranslatedString(alteration.title, this.$i18n.locale) || 
                getTranslatedString(alteration.typeOfAlteration, this.$i18n.locale))
        })
        if(valid) {
            this.closeDrawingEditor(true);
        } else {
            const alertPayload = {
                message: this.$t('drawingEditor.errors.missingTitleOrType'),
                variant: "warning"
            }
            this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
        }
    }

    private unsubscribe() {
        this.unsubscribeFrom('annotationTitle');
        this.unsubscribeFrom('media');
    }

    /**
     * Renumbers all drawings from 0 to avoid conflicts in indices
     */
    private renumberShapes() {
        this.drawings.map((shapes, index) => {
            for(const [index2, shape] of shapes.entries()) {
                //this.chooseTool(shape.type)
                shape.id = this.currentTool.generateShapeId(index, index2)
            }
        })
        this.toolBox.clear()
    }

    @Watch('drawings', { deep: true })
    private onShapesChange() {
        // Update drawings
        for(let i=0; i<this.drawings.length; i++) {
            _.set(this.alterationsCopy[i], 'drawing', JSON.stringify(this.drawings[i]))
        }
    }
}